const get = require('lodash/get')
const sortBy = require('lodash/sortBy')
const keyBy = require('lodash/keyBy')
const XLSX = require('@sheet/core')
const { parse } = require('../tools/smart-id')
const format = require('date-fns/format')
const { WAREHOUSE_DEFINITIONS } = require('../order/tools/read/warehouse-code-to-virtual')
const { batchIdToProductId } = require('./tools/product-batch-util')
const { getManufacturerProductIdFromAlias, aliasValuesOf } = require('../batch/tools/product-alias')
const { listBatches } = require('../batch/api/batch-list')
const { readsViaAgave } = require('../tools/reads-via-agave')
const { productIdToCode } = require('../product/tools/construct')
const { PLACEHOLDER_BATCH_NOS } = require('./tools/virtual-batch')
const { SHIPMENT_STATUS } = require('./constants')

const collectBatchIdsFromShipment = (shipment) => {
  const batchIds = new Set()
  if (shipment && shipment.counts) {
    Object.keys(shipment.counts).forEach(batchId => batchIds.add(batchId))
  }
  if (shipment && shipment.history) {
    Object.values(shipment.history).forEach(entry => {
      if (!entry || !entry.counts) {
        return
      }
      Object.keys(entry.counts).forEach(batchId => batchIds.add(batchId))
    })
  }
  return [...batchIds]
}

const getBatchNo = (batchId) => parse(batchId).batchNo

// A shipment's `new` snapshot and its `packed`/`sent` snapshots can be written by
// different upstream integrations that encode the same manufacturer-specific product
// in different case or batch-id segment, so getExportProductIdFromBatchId can resolve
// a different id per snapshot for what is physically the same batch. batchNo is the
// one component both integrations agree on, so it's used to force every snapshot of
// the same batch onto a single exportProductId - preferring whichever candidate
// actually resolved to a known product.
// Different products can share a batchNo (#26608), seen as one batchNo on two lines of
// a snapshot; those are grouped by generic product + batchNo (siblings never share one).
const getSnapshotBatchIds = (shipment) => {
  const snapshots = [get(shipment, 'counts', {}), ...Object.values(get(shipment, 'history', {})).map(entry => get(entry, 'counts', {}))]
  return snapshots.map(counts => Object.keys(counts || {}))
}

const isRealBatchNo = (batchNo) => batchNo && !PLACEHOLDER_BATCH_NOS.includes(batchNo)

const getBatchNosSharedByProducts = (shipment) => {
  const shared = new Set()
  getSnapshotBatchIds(shipment).forEach(batchIds => {
    const seen = new Set()
    batchIds.forEach(batchId => {
      const batchNo = getBatchNo(batchId)
      if (!isRealBatchNo(batchNo)) return
      if (seen.has(batchNo)) shared.add(batchNo)
      seen.add(batchNo)
    })
  })
  return shared
}

const getGenericProductKey = (exportProductId, productsById) => {
  const id = String(exportProductId)
  const product = productsById[id] || productsById[id.toLowerCase()]
  if (product && product.genericParent) return `product:${String(product.genericParent).toLowerCase()}`
  if (product) return String(product._id).toLowerCase()
  return id.toLowerCase()
}

const buildBatchIdToExportProductId = (shipment, getExportProductIdFromBatchId, productsById) => {
  const sharedBatchNos = getBatchNosSharedByProducts(shipment)
  const groups = {}
  collectBatchIdsFromShipment(shipment).forEach(batchId => {
    const batchNo = getBatchNo(batchId)
    if (!isRealBatchNo(batchNo)) return
    const exportProductId = getExportProductIdFromBatchId(batchId)
    const groupKey = sharedBatchNos.has(batchNo)
      ? `${getGenericProductKey(exportProductId, productsById)}\0${batchNo}`
      : batchNo
    groups[groupKey] = groups[groupKey] || []
    groups[groupKey].push({ batchId, exportProductId })
  })

  return Object.values(groups).reduce((acc, members) => {
    const canonical = members.find(({ exportProductId }) => productsById[exportProductId]) || members[0]
    members.forEach(({ batchId }) => { acc[batchId] = canonical.exportProductId })
    return acc
  }, {})
}

// All placeholder batches for a product share this key, so they still sum into one row
// (they don't identify a real distinguishable batch, so one-row-per-line would just
// produce N indistinguishable "UNKNOWN" rows instead of one honest total).
const PLACEHOLDER_BATCH_GROUP = '\0placeholder'

const getBatchGroupKey = (batchNo) => {
  if (!batchNo || PLACEHOLDER_BATCH_NOS.includes(batchNo)) return PLACEHOLDER_BATCH_GROUP
  return batchNo
}

// A row's batch identity is which side(s) of the shipment lifecycle it appeared on:
// `new` for what was ordered, everything after packing for what actually moved.
const ORDERED_STATUSES = [SHIPMENT_STATUS.NEW]
const PACKED_STATUSES = [SHIPMENT_STATUS.PACKED, SHIPMENT_STATUS.SENT, SHIPMENT_STATUS.ARRIVED, SHIPMENT_STATUS.RECEIVED]

// One row per (product, batch) rather than one row per product: a batch present on
// both the ordered and packed side lands on a single row with both quantities filled;
// a batch present on only one side still gets its own row, with the other side blank,
// instead of being silently dropped or merged into an unrelated batch's totals.
const getPlannedQuantitiesByBatch = (shipment, getExportProductIdFromBatchId) => {
  const snapshotStatus = [SHIPMENT_STATUS.ARRIVED, SHIPMENT_STATUS.NEW, SHIPMENT_STATUS.PACKED, SHIPMENT_STATUS.RECEIVED, SHIPMENT_STATUS.SENT]
  const rows = {}

  const history = (shipment && shipment.history) ? shipment.history : {}
  for (const delivery of Object.values(history)) {
    for (const status of snapshotStatus) {
      const snapshotDate = shipment.snapshotDates[status]
      if (snapshotDate && snapshotDate === delivery.createdAt) {
        if (!delivery || !delivery.counts) continue
        for (const [batchId, { quantity }] of Object.entries(delivery.counts)) {
          // For PSM, export product identity should follow manufacturer-derived product when present.
          // For non-PSM tenants, export product identity stays generic.
          const exportProductId = getExportProductIdFromBatchId(batchId)
          const batchNo = getBatchNo(batchId)
          const rowKey = `${exportProductId}\0${getBatchGroupKey(batchNo)}`

          rows[rowKey] = rows[rowKey] || {
            exportProductId,
            quantities: {},
            orderedBatchNos: new Set(),
            packedBatchNos: new Set()
          }
          const row = rows[rowKey]
          row.quantities[status] = (row.quantities[status] || 0) + quantity

          if (batchNo) {
            if (ORDERED_STATUSES.includes(status)) row.orderedBatchNos.add(batchNo)
            if (PACKED_STATUSES.includes(status)) row.packedBatchNos.add(batchNo)
          }
        }
      }
    }
  }

  return Object.values(rows)
}

// A status genuinely absent from a row (that side of the shipment never touched this
// batch) reads as a blank cell, not 0 - 0 would claim we confirmed none moved, when
// really that side has no record of this batch at all.
const getQuantityOrBlank = (statusQuantities, status) => get(statusQuantities, status, '')

const getPositiveNumber = (product, field) => {
  const value = Number(get(product, field))
  return value > 0 ? value : 0
}

// Per-product figures (volume, price) prefer the manufacturer-specific product when it
// carries a positive value, otherwise fall back to the generic product's, and 0 when
// neither has one. manufacturerProduct is only set for PSM rows resolved via batch alias.
const getProductFigure = (field, { manufacturerProduct, genericProduct }) =>
  getPositiveNumber(manufacturerProduct, field) || getPositiveNumber(genericProduct, field)

const formatDate = (date) => {
  if (!date) return

  const newDate = new Date(date)
  return format(newDate, 'YYYY-MM-DD')
}

// Build a map of routeId -> assigned driver name.
// The truthful "assigned driver" lives on the user doc (user.routes[]), joined by route.
// This mirrors the routes UI enrichment (RoutesApi._addDriverInformation) so the export and
// the routes screen agree on who the driver is. Best-effort: on failure we return an empty map
// and the Driver Name column falls back to blank rather than failing the whole export.
const getDriverNamesByRouteId = async (api, shipments) => {
  const routeIds = [...new Set(shipments.map(shipment => get(shipment, 'route.id', '')).filter(Boolean))]
  if (routeIds.length === 0) {
    return {}
  }

  try {
    const drivers = await api.user.listUsersByRoutes({ routes: routeIds })
    return (drivers || []).reduce((acc, driver) => {
      const driverName = get(driver, 'fullName') || get(driver, 'name')
      const driverRoutes = get(driver, 'routes', []) || []
      driverRoutes.forEach(routeId => {
        if (routeIds.includes(routeId)) {
          acc[routeId] = driverName
        }
      })
      return acc
    }, {})
  } catch (_) {
    return {}
  }
}

const exportLMDShipmentsData = async function (state, {api, shipments, tenant: TENANT_FROM_WEB}) {
  // Only use manufacturer-specific product volumetrics for PSM tenant (not sub-tenants)
  // Validate tenant is a string before calling toLowerCase to avoid runtime errors
  const useManufacturerSpecificProducts = TENANT_FROM_WEB && typeof TENANT_FROM_WEB === 'string' && TENANT_FROM_WEB.toLowerCase() === 'psm'

  // Collect both generic and manufacturer-specific product IDs.
  // Also build map for efficient lookup:
  // - shipmentExportProductIdToGenericProductId: array indexed by shipment index, maps export product id -> generic product id
  // - shipmentBatchIdToManufacturerProductId: array indexed by shipment index, maps batchId -> manufacturerProductId
  const productIds = new Set()
  const shipmentExportProductIdToGenericProductId = []
  const shipmentBatchIdToManufacturerProductId = []

  shipments.forEach((shipment, shipmentIndex) => {
    const exportProductIdToGenericProductId = {}
    const batchIdToManufacturerProductId = {}
    const batchIds = collectBatchIdsFromShipment(shipment)
    batchIds.forEach(batchId => {
      // Use batchIdToProductId for consistent parsing
      const genericProductId = batchIdToProductId(batchId)
      productIds.add(genericProductId)
      // Always map generic product id to itself (fallback export identity)
      exportProductIdToGenericProductId[genericProductId] = genericProductId
    })
    shipmentExportProductIdToGenericProductId[shipmentIndex] = exportProductIdToGenericProductId
    shipmentBatchIdToManufacturerProductId[shipmentIndex] = batchIdToManufacturerProductId
  })

  // Resolve the driver assigned to each route (issue 23439). "Driver Name" reflects the
  // route assignment; the received snapshot's created_by becomes the separate "Received By" column.
  const driverNamesByRouteId = await getDriverNamesByRouteId(api, shipments)

  const productsViaAgave = readsViaAgave(state, 'product')
  // getByIds resolves its own source: the local pouch for a legacy user, PG
  // over agave for an online-only one, who never replicates that pouch and used
  // to get blank Product Name / Code / volume / price cells here.
  const allProductsDoc = await api.product.getByIds([...productIds])
  const productsById = keyBy(allProductsDoc, '_id')

  // For product IDs not found by direct lookup, try alias-based fallback.
  // Some shipment types (e.g. PMI malaria) use vendor/variant codes like 'GHSC-MAL0002C'
  // as the product code in batch IDs, which don't match the canonical CouchDB _id
  // (e.g. 'product:ghsc-mal0002') but ARE present in that product's alias.one array.
  //
  // Skipped over agave: this is a second raw pouch read, so for the online-only
  // user it would answer [] no matter what. Their unresolved ids fall through to
  // the identifier below instead.
  //
  // The productsViaAgave guard goes away with #25511, which teaches the
  // PG-backed getByIds to resolve aliases: the pass will then answer for an
  // online-only user too, and can just run for everyone.
  const missingProductIds = productsViaAgave ? [] : [...productIds].filter(id => !productsById[id])
  if (missingProductIds.length > 0) {
    try {
      const aliasDocs = await api.product.getByIds(missingProductIds, { resolveAlias: { 'one': true } })
      aliasDocs.forEach(product => {
        const oneAliases = get(product, 'alias.one', [])
        oneAliases.forEach(alias => {
          const aliasProductId = `product:${alias}`
          if (missingProductIds.includes(aliasProductId)) {
            productsById[aliasProductId] = product
          }
        })
      })
    } catch (e) {
      // Alias lookup is best-effort; continue with whatever was found directly
    }
  }

  // Resolve each batch to its manufacturer-specific product from the batch doc's
  // Couch master-data `alias`. The `:manufacturer:` segment is never used to
  // infer the product (it is often a name, e.g. `chemonics`). Only when no alias
  // resolves does the export fall back to the generic product id (issue #26027).
  if (useManufacturerSpecificProducts) {
    const allBatchIds = shipments.flatMap(shipment => collectBatchIdsFromShipment(shipment))
    try {
      // Batch docs live in Couch `master-data` (NOT integrated-data); they carry
      // the `alias` mapping a manufacturer name to the product code.
      const batchDocById = keyBy(await listBatches(state, { ids: allBatchIds }), '_id')

      // Alias-derived product ids are not part of the generic productIds set, so
      // fetch them before resolving.
      const aliasProductIdsToFetch = new Set()
      for (const batchId of allBatchIds) {
        const batch = batchDocById[batchId]
        if (!batch) continue
        for (const aliasValue of aliasValuesOf(batch.alias)) {
          aliasProductIdsToFetch.add(`product:${aliasValue.toLowerCase()}`)
        }
      }
      if (aliasProductIdsToFetch.size > 0) {
        const aliasProducts = await api.product.getByIds([...aliasProductIdsToFetch])
        Object.assign(productsById, keyBy(aliasProducts, '_id'))
      }

      shipmentBatchIdToManufacturerProductId.forEach((batchIdMap, shipmentIndex) => {
        const exportProductIdToGenericProductId = shipmentExportProductIdToGenericProductId[shipmentIndex] || {}
        for (const batchId of collectBatchIdsFromShipment(shipments[shipmentIndex])) {
          const genericProductId = batchIdToProductId(batchId)
          const resolvedProductId = getManufacturerProductIdFromAlias(batchDocById[batchId], {
            genericProductId,
            productsById
          })
          if (resolvedProductId && productsById[resolvedProductId]) {
            batchIdMap[batchId] = resolvedProductId
            exportProductIdToGenericProductId[resolvedProductId] = genericProductId
          }
        }
      })
    } catch (e) {
      // Batch resolution is best-effort; unresolved entries fall back to generic.
    }
  }

  // Get all destination locations and key by _id for safe lookup
  // (getByIds filters out missing docs, which shifts indices)
  const destinationIds = shipments.map(shipment => get(shipment, 'destination.id', ''))
  const uniqueDestinationIds = [...new Set(destinationIds.filter(Boolean))]
  const destinationLocations = uniqueDestinationIds.length
    ? await api.location.getByIds(uniqueDestinationIds)
    : []
  const destinationsById = keyBy(destinationLocations, '_id')

  const shipmentLineExport = []
  for (const [index, shipment] of shipments.entries()) {
    const destination = destinationsById[get(shipment, 'destination.id', '')]
    const destinationId = get(shipment, 'destination.id', '')
    const { state: destinationState, lga } = parse(destinationId)

    const originId = get(shipment, 'origin.id', '')
    const warehouse = Object.values(WAREHOUSE_DEFINITIONS[TENANT_FROM_WEB] || {}).find(wh => originId.includes(wh._id))

    const exportProductIdToGenericProductId = shipmentExportProductIdToGenericProductId[index] || {}
    const batchIdToManufacturerProductId = shipmentBatchIdToManufacturerProductId[index] || {}

    const getExportProductIdFromBatchId = (batchId) => {
      const genericProductId = batchIdToProductId(batchId)
      if (!useManufacturerSpecificProducts) return genericProductId
      const manufacturerProductId = batchIdToManufacturerProductId[batchId]
      return manufacturerProductId || genericProductId
    }

    const batchIdToExportProductId = buildBatchIdToExportProductId(
      shipment,
      getExportProductIdFromBatchId,
      productsById
    )
    const getReconciledExportProductIdFromBatchId = (batchId) =>
      batchIdToExportProductId[batchId] || getExportProductIdFromBatchId(batchId)

    const plannedRows = getPlannedQuantitiesByBatch(shipment, getReconciledExportProductIdFromBatchId)

    plannedRows.forEach(({ exportProductId, quantities: statusQuantities, orderedBatchNos, packedBatchNos }) => {
      // Export identity:
      // - For PSM: prefer manufacturer-derived product id (brand) when present in batch id
      // - For non-PSM: always generic product id
      const exportedProduct = productsById[exportProductId]

      const genericProductId = exportProductIdToGenericProductId[exportProductId] || exportProductId
      const genericProduct = productsById[genericProductId]
      const productDetail = exportedProduct || genericProduct

      // Volume and price:
      // - For PSM: prefer the manufacturer-derived product's value when present and positive (> 0),
      //   otherwise fall back to the generic product's
      // - For non-PSM: use the generic product's
      const hasManufacturerIdentity = useManufacturerSpecificProducts &&
        exportProductIdToGenericProductId[exportProductId] &&
        exportProductIdToGenericProductId[exportProductId] !== exportProductId
      const figureProducts = {
        manufacturerProduct: hasManufacturerIdentity ? exportedProduct : undefined,
        genericProduct
      }

      shipmentLineExport.push({
        orderId: get(shipment, 'orderId', ''),
        route: get(shipment, 'route.name', ''),
        shipmentNo: get(shipment, 'shipmentNo', ''),
        facilityCode: get(destination, 'additionalData.warehouseCode', ''),
        facilityName: get(destination, 'name', ''),
        facilityState: destinationState,
        facilityLGA: lga,
        warehouseCode: get(warehouse, 'warehouseCode', ''),
        warehouseName: get(warehouse, 'name', ''),
        // Fall back to the identifier the read asked with, so a product we
        // could not resolve gives an identifiable row rather than an anonymous
        // one. Same derivation `construct` uses for a product doc carrying no
        // explicit code.
        productId: get(productDetail, '_id', exportProductId),
        productName: get(productDetail, 'fullName', ''),
        productCode: get(productDetail, 'code', productIdToCode(exportProductId)),
        orderedBatch: [...orderedBatchNos].sort().join(', '),
        packedBatch: [...packedBatchNos].sort().join(', '),
        quantityOrdered: getQuantityOrBlank(statusQuantities, 'new'),
        quantityPacked: getQuantityOrBlank(statusQuantities, 'packed'),
        quantityReceived: getQuantityOrBlank(statusQuantities, 'received'),
        unitVolume: getProductFigure('unitVolume', figureProducts),
        unitPrice: getProductFigure('unitPrice', figureProducts),
        funderId: get(shipment, 'funder.id', ''),
        programId: get(shipment, 'programId', ''),
        carrierName: get(shipment, 'carrierName', ''),
        // Driver Name: the driver assigned to the route (route -> _users lookup).
        driverName: driverNamesByRouteId[get(shipment, 'route.id', '')] || '',
        // Received By: the collector for a self-pickup hand-off, otherwise whoever
        // submitted the received snapshot (shipment.driverName is sourced from the
        // received snapshot's created_by).
        receivedBy: get(shipment, 'collector.name') || get(shipment, 'driverName', ''),
        status: get(shipment, 'status', ''),
        createdAt: formatDate(get(shipment, 'createdAt')),
        pickUpDate: formatDate(get(shipment, 'snapshotDates.sent', '')),
        deliveryDate: formatDate(get(shipment, 'snapshotDates.received', ''))
      })
    })
  }

  const sortedLMDs = sortBy(shipmentLineExport, ['destinationId', 'createdAt'])

  const columns = [
    {
      name: 'Order ID',
      key: 'orderId'
    }, {
      name: 'Route',
      key: 'route'
    }, {
      name: 'Shipment No',
      key: 'shipmentNo'
    }, {
      name: 'Warehouse Code',
      key: 'warehouseCode'
    }, {
      name: 'Warehouse Name',
      key: 'warehouseName'
    }, {
      name: 'Facility Code',
      key: 'facilityCode'
    }, {
      name: 'Facility Name',
      key: 'facilityName'
    }, {
      name: 'Facility State',
      key: 'facilityState'
    }, {
      name: 'Facility LGA',
      key: 'facilityLGA'
    }, {
      name: 'Product ID',
      key: 'productId'
    }, {
      name: 'Product Name',
      key: 'productName'
    }, {
      name: 'Product Code',
      key: 'productCode'
    }, {
      name: 'Ordered Batch',
      key: 'orderedBatch'
    }, {
      name: 'Packed Batch',
      key: 'packedBatch'
    }, {
      name: 'Quantity Ordered',
      key: 'quantityOrdered'
    }, {
      name: 'Quantity Packed',
      key: 'quantityPacked'
    }, {
      name: 'Quantity Received',
      key: 'quantityReceived'
    }, {
      name: 'Unit Volume',
      key: 'unitVolume'
    }, {
      name: 'Unit Price',
      key: 'unitPrice'
    }, {
      name: 'Funder',
      key: 'funderId'
    }, {
      name: 'Program',
      key: 'programId'
    }, {
      name: 'Carrier Name',
      key: 'carrierName'
    }, {
      name: 'Driver Name',
      key: 'driverName'
    }, {
      name: 'Received By',
      key: 'receivedBy'
    }, {
      name: 'Status',
      key: 'status'
    }, {
      name: 'Creation Date',
      key: 'createdAt'
    },
    {
      name: 'Pickup Date',
      key: 'pickUpDate'
    },
    {
      name: 'Delivery Date',
      key: 'deliveryDate'
    }
  ]

  const headers = columns.map(column => column.name)
  const data = sortedLMDs.map(event => {
    return columns.map(column => {
      return get(event, column.key, '')
    })
  })
  return [headers].concat(data)
}

const generateExcelSheet = (aoa) => {
  return new Promise((resolve) => {
    const ws = XLSX.utils.aoa_to_sheet(aoa)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'last-mile-deliveries-export')
    const wbout = XLSX.write(wb, {
      bookType: 'xlsx',
      bookSST: false,
      type: 'array'
    })
    resolve(new global.Blob([wbout], {type: 'application/octet-stream'}))
  })
}

async function exportLMDShipments (state, params) {
  const data = await exportLMDShipmentsData(state, params)
  if (params.dryRun) return data

  return generateExcelSheet(data)
}
module.exports = { exportLMDShipments }
